/**
 * Plate numbers — how a confirmation about a vehicle is found again.
 *
 * A plate is stored normalised (upper case, no spaces or dashes) so "abc 1234",
 * "ABC-1234" and "ABC1234" are the same plate to every query, and displayed
 * with the space LTO prints between its letter and number groups. The patterns
 * are the ones the R5 portal's status lookup accepts, so a plate a citizen can
 * type there is a plate staff can record here.
 */

import { z } from 'zod';

const PLATE_PATTERNS = [
  /^[A-Z]{3}\d{3,4}$/, // ABC 1234 / ABC 123 (four-wheel)
  /^[A-Z]{2}\d{4,5}$/, // AB 12345 (motorcycle, 2014 series)
  /^\d{3,4}[A-Z]{2,3}$/, // 1234 AB / 123 ABC (older motorcycle)
  /^[A-Z]\d[A-Z]\d{3,4}$/, // A1B 2345 (newer motorcycle)
] as const;

/** Upper case, without spaces, dashes, dots or underscores. */
export function normalizePlateNumber(raw: string): string {
  return raw.normalize('NFKC').trim().toUpperCase().replace(/[\s\-_.]/g, '');
}

export function isPlateNumber(normalized: string): boolean {
  return PLATE_PATTERNS.some((pattern) => pattern.test(normalized));
}

/** "ABC1234" → "ABC 1234": a space where letters meet digits, as on the plate. */
export function formatPlateNumber(normalized: string): string {
  return normalized.replace(/^([A-Z]+)(\d+)$/, '$1 $2').replace(/^(\d+)([A-Z]+)$/, '$1 $2');
}

/** Accepts any spacing the user types; yields the normalised plate, or fails with a readable message. */
export const plateNumberSchema = z
  .string()
  .max(20)
  .transform(normalizePlateNumber)
  .refine(isPlateNumber, { message: 'Enter a plate number such as ABC 1234, AB 12345 or 1234 AB.' });
